import { useEffect, useState } from 'react';
import { LEVEL_LABEL, UserReach, userReachApi } from '../../api/access';

/**
 * What one person can reach, and by what route.
 *
 * The screen an Admin opens when somebody says "I can't see it". Every line
 * names HOW — a group, a direct grant with who added it and when, or the open
 * base access doing the work — because "they have access" is not an answer to
 * "why can they?", and the fix depends entirely on which of the three it is.
 *
 * Read-only, deliberately. Nothing here is editable: the place a grant changes
 * is the share dialog on the thing it is a grant on, and an editable copy here
 * would be a second way to say the same thing that could disagree with the first.
 *
 * It also lists what they CANNOT reach. That is usually the answer — "nobody
 * shared it" rather than "the grant is wrong" — and counting it beats making
 * somebody subtract two numbers in their head.
 */
export function ReachPanel({ userId, onClose }: { userId: number; onClose: () => void }) {
  const [reach, setReach] = useState<UserReach | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    userReachApi(userId).then(setReach).catch((e) => setError(e.message));
  }, [userId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="fs-scrim" onClick={onClose} />
      <div className="fs-drawer" role="dialog" aria-modal="true">
        <div className="fs-drawer-head">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: 'var(--text-card-title)' }}>
                What {reach?.user.name || 'they'} can reach
              </h3>
              {reach && <div className="fs-cell-sub">{reach.user.email}</div>}
            </div>
            <button className="fs-btn icon" title="Close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="fs-drawer-body">
          {error && <div className="ad-err">{error}</div>}
          {!reach && !error && <div>Loading…</div>}

          {reach?.everything && (
            <div className="fs-consequence">
              <b>Every form and folder in the account.</b> Owners and Admins bypass grants,
              so there is nothing here to share or unshare. Make them a Member to
              decide what they can reach.
            </div>
          )}

          {reach && !reach.everything && (
            <>
              <Section
                title="Folders"
                empty="No folders are shared with them."
                lines={reach.folders}
              />
              <Section
                title="Forms"
                empty="No forms are shared with them."
                lines={reach.forms}
              />

              {!!reach.unreachable.length && (
                <div style={{ marginTop: 20 }}>
                  <div className="fs-field"><label>Not reachable · {reach.unreachable.length}</label></div>
                  <div className="fs-cell-sub" style={{ lineHeight: 1.7 }}>
                    {reach.unreachable.map((f) => f.name).join(', ')}
                  </div>
                  <div className="fs-cell-sub faint" style={{ marginTop: 8 }}>
                    Usually the answer: nobody has shared these, rather than a grant
                    being wrong. Share a folder to fix several at once.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="fs-drawer-foot">
          <button className="fs-btn neutral" onClick={onClose}>Close</button>
          <span className="fs-cell-sub faint">
            Read-only. Access changes on the form or folder it belongs to.
          </span>
        </div>
      </div>
    </>
  );
}

function Section({ title, empty, lines }: {
  title: string;
  empty: string;
  lines: UserReach['forms'];
}) {
  return (
    <div style={{ marginTop: 18 }}>
      <div className="fs-field"><label>{title} · {lines.length}</label></div>
      {!lines.length && <div className="fs-cell-sub">{empty}</div>}
      {lines.map((l) => (
        <div
          key={l.id}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 0', borderBottom: '1px solid var(--border-soft)',
          }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>
            <b style={{ fontSize: 'var(--text-body)' }}>{l.name}</b>
            <div className="fs-cell-sub">
              {l.folder ? `in ${l.folder} · ` : ''}{l.route}
            </div>
          </span>
          <span className="fs-pill identity">{LEVEL_LABEL[l.level]}</span>
        </div>
      ))}
    </div>
  );
}
